import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Statistics() {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/statistics`)
      .then(res => setStatistics(res.data))
      .catch(() => toast.error('Failed to load statistics'))
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/statistics/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'speedread-stats.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Exported successfully');
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-xl">Reading Statistics</h1>
              <p className="text-sm text-muted-foreground">Track your progress</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleExport} className="gap-2" data-testid="export-btn">
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading statistics...</p>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard title="Documents" value={statistics?.total_documents || 0} />
              <StatCard title="Words Read" value={(statistics?.total_words_read || 0).toLocaleString()} />
              <StatCard title="Reading Time" value={`${statistics?.total_reading_time_minutes || 0} min`} />
              <StatCard title="Avg. WPM" value={statistics?.average_wpm || '-'} />
              <StatCard title="Completed" value={statistics?.documents_completed || 0} />
            </div>

            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="font-heading font-semibold text-lg mb-4">Recent Sessions</h2>
              {statistics?.recent_sessions?.length > 0 ? (
                <div className="space-y-3">
                  {statistics.recent_sessions.map((s, i) => (
                    <SessionItem key={i} session={s} />
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No sessions yet. Start reading!</p>
              )}
            </div>

            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="font-heading font-semibold text-lg mb-4">Speed Reading Tips</h2>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Start at 200-300 WPM and increase gradually</li>
                <li>• Focus on the highlighted ORP letter</li>
                <li>• Practice 15-20 minutes daily</li>
                <li>• Use dark mode to reduce eye strain</li>
              </ul>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ title, value }) {
  return (
    <div className="bg-card rounded-xl border border-border p-4">
      <p className="text-2xl font-bold text-primary">{value}</p>
      <p className="text-sm text-muted-foreground">{title}</p>
    </div>
  );
}

function SessionItem({ session }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <div>
        <p className="font-medium">{session.words_read.toLocaleString()} words</p>
        <p className="text-sm text-muted-foreground">
          {new Date(session.timestamp).toLocaleDateString()}
        </p>
      </div>
      <div className="text-right">
        <p className="font-medium text-primary">{session.average_wpm} WPM</p>
        <p className="text-sm text-muted-foreground">{Math.round(session.duration_seconds / 60)} min</p>
      </div>
    </div>
  );
}
